import type { Subscription } from "~/types/BillingTypes";

const FEATURE_FALLBACK_SLUGS: Record<string, readonly string[]> = {
  keyword_clustering: [
    "trial",
    "starter",
    "pro",
    "business",
    "bespoke",
    "enterprise",
    "agency_trial",
    "premium",
    "elite",
    "ultimate",
    "max",
  ],
  ai_traffic_analytics: [
    "pro",
    "business",
    "bespoke",
    "enterprise",
    "agency_trial",
    "premium",
    "elite",
    "ultimate",
    "max",
  ],
  backlink_research: [
    "pro",
    "business",
    "bespoke",
    "enterprise",
    "agency_trial",
    "premium",
    "elite",
    "ultimate",
    "max",
  ],
  sitemap_indexing: [
    "starter",
    "pro",
    "business",
    "bespoke",
    "enterprise",
    "agency_trial",
    "premium",
    "elite",
    "ultimate",
    "max",
  ],
};

export function isBespokePlan(
  subscription: Subscription | null | undefined
): boolean {
  return (subscription?.billing_plan?.slug ?? "").toLowerCase() === "bespoke";
}

export function entitlementConfig(
  subscription: Subscription | null | undefined
): Record<string, boolean | number | string> | null | undefined {
  if (!subscription) return undefined;
  if (subscription.is_enterprise_plan) {
    return subscription.enterprise_config;
  }
  if (isBespokePlan(subscription)) {
    return subscription.bespoke_config;
  }
  return undefined;
}

export function hasPlanFeature(
  subscription: Subscription | null | undefined,
  key: string
): boolean {
  if (
    !subscription ||
    subscription.missing_subscription ||
    !subscription.is_active
  ) {
    return false;
  }

  if (subscription.is_enterprise_plan || isBespokePlan(subscription)) {
    const cfg = entitlementConfig(subscription);
    if (cfg != null && key in cfg) {
      return cfg[key] === true;
    }
  }

  if (subscription.is_agency_plan) {
    const cfg = subscription.agency_entitlements;
    if (cfg != null && key in cfg) {
      return cfg[key] === true;
    }
  }

  const fromPlan = subscription.billing_plan?.features?.[key];
  if (typeof fromPlan === "boolean") {
    return fromPlan;
  }

  const slug = (subscription.billing_plan?.slug ?? "").toLowerCase();
  return (FEATURE_FALLBACK_SLUGS[key] ?? []).includes(slug);
}

export function aiVisibilityPlatformLimit(
  subscription: Subscription | null | undefined
): number {
  const fromConfig = entitlementConfig(subscription)?.ai_visibility_platforms_limit;
  if (typeof fromConfig === "number" && fromConfig > 0) {
    return fromConfig;
  }

  const fromPlan =
    subscription?.billing_plan?.features?.ai_visibility_platforms_limit;
  if (typeof fromPlan === "number" && fromPlan > 0) {
    return fromPlan;
  }

  return 4;
}
